import * as Admin from '.';
import * as Inventory from '../inventory';
import { action } from '@play-co/replicant';
import createActions from '../config/actions/createActions';
import { grantMatch2Rewards } from '../match2/match2.modifiers';
import * as Fate from '../fate';
import { shopBundleGetPackRewardSet } from '../match2/shopbundles/match2-shopbundles.getters';
import { ProductId } from '../iap/iap.ruleset';
import { match2BundleSaleGetPackRewardSet } from '../match2/bundlesale/match2-bundlesale.getters';
import { BundleSaleProductId } from '../match2/bundlesale/match2-bundlesale.ruleset';
import type { MutableState } from '../State';

/** Drop already-claimed messages, then mark the current batch as claimed. */
function sweepClaimedMessages(state: MutableState) {
  state.admin = state.admin.filter((item) => !item.claimed);

  for (const message of state.admin) {
    message.claimed = true;
  }
}

export default createActions({
  /**
   * Consume gems and give points by provided exchange rate
   */
  claimAdminMessageResources: action((state, _, api) => {
    const now = api.date.now();

    // Get resources counts
    const unclaimed = Admin.getters.getUnclaimedMessages(state);

    if (
      !unclaimed.coins &&
      !unclaimed.gems &&
      !unclaimed.newPetName &&
      !unclaimed.allowEditor &&
      !unclaimed.match2Rewards.length &&
      !unclaimed.battlePass &&
      !unclaimed.resetPetNegativeEffects &&
      !unclaimed.superLollipopStreak &&
      !unclaimed.salePackProductIds.length &&
      !unclaimed.bundleSalePackProductIds.length
    ) {
      // Messages that reduce to nothing claimable must still clear, or the
      // launch sequence retries this claim forever (#11797).
      if (Admin.getters.hasUnclaimedMessages(state)) {
        sweepClaimedMessages(state);
        return;
      }

      throw new Error('No admin message resources to claim.');
    }

    // Add b-points
    if (unclaimed.coins > 0) {
      Inventory.modifiers.addPoints(
        state,
        unclaimed.coins,
        { feature: 'admin_message' },
        api,
      );
    }

    // Add gems
    if (unclaimed.gems > 0) {
      Inventory.modifiers.addFreeGems(
        state,
        unclaimed.gems,
        { feature: 'admin_message' },
        api,
      );
    }

    // Claim match2 rewards
    if (unclaimed.match2Rewards) {
      grantMatch2Rewards(
        state,
        unclaimed.match2Rewards,
        { feature: 'admin_message' },
        api,
      );
    }

    // Reset pet negative effects
    if (unclaimed.resetPetNegativeEffects) {
      Fate.modifiers.resolvePetNegativeEffects(state, now);
    }

    // Recorver Super Lollipop streak - only if user has the feature and streak is higher than current
    if (
      state.superLollipop &&
      unclaimed.superLollipopStreak > state.superLollipop.winStreak
    ) {
      state.superLollipop.winStreak = unclaimed.superLollipopStreak;
    }

    // Claim sale pack rewards
    if (unclaimed.salePackProductIds.length > 0) {
      // Grant paid gems
      if (unclaimed.salePackPaidGems > 0) {
        Inventory.modifiers.addPaidGems(
          state,
          unclaimed.salePackPaidGems,
          { feature: 'admin_message' },
          api,
        );
      }

      // Grant free gems (separate from regular admin freeGems)
      if (unclaimed.salePackFreeGems > 0) {
        Inventory.modifiers.addFreeGems(
          state,
          unclaimed.salePackFreeGems,
          { feature: 'admin_message' },
          api,
        );
      }

      // Grant pack bonus rewards (powerups, boosters, energy) for each product
      for (const productId of unclaimed.salePackProductIds) {
        const rewards = shopBundleGetPackRewardSet(productId as ProductId, now);
        if (rewards.length > 0) {
          grantMatch2Rewards(state, rewards, { feature: 'admin_message' }, api);
        }
      }

      // Set starter pack flags if applicable
      if (unclaimed.setStarterPackPurchased) {
        if (!state.match2.shopBundles) state.match2.shopBundles = {};
        state.match2.shopBundles.starterPackPurchased = true;
      }

      if (unclaimed.setPetStarterPackPurchased) {
        if (!state.match2.shopBundles) state.match2.shopBundles = {};
        state.match2.shopBundles.petStarterPackPurchased = true;
      }
    }

    // Claim bundle sale reimbursement rewards
    if (unclaimed.bundleSalePackProductIds.length > 0) {
      // Grant paid gems from paid bundle sale products
      if (unclaimed.bundleSalePackPaidGems > 0) {
        Inventory.modifiers.addPaidGems(
          state,
          unclaimed.bundleSalePackPaidGems,
          { feature: 'admin_message' },
          api,
        );
      }

      // Grant free gems from product rewards table
      if (unclaimed.bundleSalePackFreeGems > 0) {
        Inventory.modifiers.addFreeGems(
          state,
          unclaimed.bundleSalePackFreeGems,
          { feature: 'admin_message' },
          api,
        );
      }

      // Grant product rewards for paid pack + free follow-up products
      for (const productId of unclaimed.bundleSalePackProductIds) {
        const rewards = match2BundleSaleGetPackRewardSet(
          productId as BundleSaleProductId,
          now,
        );
        if (rewards.length > 0) {
          grantMatch2Rewards(state, rewards, { feature: 'admin_message' }, api);
        }
      }
    }

    sweepClaimedMessages(state);
  }),
});
